import type { CustomFieldType } from '@payaca/custom-fields';

/**
 * Operators recognised by the `customFieldCondition` pipeline-guard variant.
 *
 * The order here is the on-wire / authoring order — operator dropdowns in
 * the Provider App use `OPERATORS_BY_FIELD_TYPE[type]` (which preserves
 * declaration order) so changing this list reorders the UI.
 */
export const CUSTOM_FIELD_CONDITION_OPERATORS = [
  'equals',
  'notEquals',
  'isSet',
  'isNotSet',
  'allChildrenAreSet',
  'allChildrenAreNotSet',
  'anyChildIsSet',
] as const;

export type CustomFieldConditionOperator =
  (typeof CUSTOM_FIELD_CONDITION_OPERATORS)[number];

/**
 * Single source of truth for which operators apply to which `CustomFieldType`.
 * Consumed by:
 *  - the Provider App, to filter the operator dropdown by the picked field
 *  - the server's `validateCustomFieldConditionConfig`, to reject invalid
 *    combinations at write time
 *  - the server's runtime evaluators, to fail closed when a field's type
 *    has drifted away from the operator's allow-list
 *
 * The matrix unifies "shape compatibility" (a fieldset operator only
 * accepts the `'fieldset'` type; non-fieldset types only accept leaf
 * operators) and "per-type allow-list for equality" (the equality
 * operators are scoped to types where guard equality is meaningful, e.g.
 * not URL or textarea) into a single key lookup.
 */
export const OPERATORS_BY_FIELD_TYPE = {
  text: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  email: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  number: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  boolean: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  select: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  user: ['equals', 'notEquals', 'isSet', 'isNotSet'],
  textarea: ['isSet', 'isNotSet'],
  url: ['isSet', 'isNotSet'],
  duration: ['isSet', 'isNotSet'],
  upload: ['isSet', 'isNotSet'],
  'multi-upload': ['isSet', 'isNotSet'],
  fieldset: ['allChildrenAreSet', 'allChildrenAreNotSet', 'anyChildIsSet'],
} as const satisfies Record<
  CustomFieldType,
  readonly CustomFieldConditionOperator[]
>;

type OperatorsByFieldType = typeof OPERATORS_BY_FIELD_TYPE;

// Compile-time check: every CustomFieldConditionOperator literal must appear
// in at least one cell of OPERATORS_BY_FIELD_TYPE. The Record<CustomFieldType, ...>
// constraint covers key completeness, but it doesn't enforce operator coverage.
type _CoveredOperators = OperatorsByFieldType[CustomFieldType][number];
type _AssertOperatorsCovered = [CustomFieldConditionOperator] extends [
  _CoveredOperators,
]
  ? true
  : 'ERROR: CustomFieldConditionOperator has values missing from OPERATORS_BY_FIELD_TYPE';
const _assertOperatorsCovered: _AssertOperatorsCovered = true;
void _assertOperatorsCovered;

/**
 * Single source of per-operator metadata: the GraphQL predicate
 * `__typename` and whether the operator's @oneOf input variant carries a
 * `comparisonValue`. Other dispatch tables (the read-boundary typename →
 * operator map, the FE/BE Zod's "value required" check, the GraphQL
 * predicate resolver) are derived from this so adding an operator only
 * requires updating one cell here plus the canonical operator tuple.
 *
 * `Record<CustomFieldConditionOperator, ...>` enforces full coverage at
 * compile time — no separate drift assertion needed.
 */
export const OPERATOR_META = {
  equals: {
    typename: 'EqualsCustomFieldConditionPredicate',
    carriesValue: true,
  },
  notEquals: {
    typename: 'NotEqualsCustomFieldConditionPredicate',
    carriesValue: true,
  },
  isSet: {
    typename: 'IsSetCustomFieldConditionPredicate',
    carriesValue: false,
  },
  isNotSet: {
    typename: 'IsNotSetCustomFieldConditionPredicate',
    carriesValue: false,
  },
  allChildrenAreSet: {
    typename: 'AllChildrenAreSetCustomFieldConditionPredicate',
    carriesValue: false,
  },
  allChildrenAreNotSet: {
    typename: 'AllChildrenAreNotSetCustomFieldConditionPredicate',
    carriesValue: false,
  },
  anyChildIsSet: {
    typename: 'AnyChildIsSetCustomFieldConditionPredicate',
    carriesValue: false,
  },
} as const satisfies Record<
  CustomFieldConditionOperator,
  { typename: string; carriesValue: boolean }
>;

export type CustomFieldConditionPredicateTypename =
  (typeof OPERATOR_META)[CustomFieldConditionOperator]['typename'];

/**
 * Maps each `CustomFieldConditionPredicate` GraphQL union member's
 * `__typename` to the operator literal it represents. Used at the
 * read boundary to normalise the discriminated predicate variants
 * returned from queries down to the operator strings the matrix is
 * keyed by. Hand-written to keep `Object.fromEntries` casts out, with a
 * mapped-type `satisfies` constraint that fails to compile if it drifts
 * from `OPERATOR_META`.
 */
export const PREDICATE_TYPENAME_TO_OPERATOR = {
  EqualsCustomFieldConditionPredicate: 'equals',
  NotEqualsCustomFieldConditionPredicate: 'notEquals',
  IsSetCustomFieldConditionPredicate: 'isSet',
  IsNotSetCustomFieldConditionPredicate: 'isNotSet',
  AllChildrenAreSetCustomFieldConditionPredicate: 'allChildrenAreSet',
  AllChildrenAreNotSetCustomFieldConditionPredicate: 'allChildrenAreNotSet',
  AnyChildIsSetCustomFieldConditionPredicate: 'anyChildIsSet',
} as const satisfies {
  [K in CustomFieldConditionOperator as (typeof OPERATOR_META)[K]['typename']]: K;
};

/**
 * Subset of operators whose @oneOf input variant carries a
 * `comparisonValue`. Derived from `OPERATOR_META` so the
 * matrix-of-record stays the only place to update.
 */
export type CustomFieldConditionOperatorCarryingValue = {
  [K in CustomFieldConditionOperator]: (typeof OPERATOR_META)[K]['carriesValue'] extends true
    ? K
    : never;
}[CustomFieldConditionOperator];

/**
 * Type predicate: whether the operator's @oneOf predicate input carries
 * a `comparisonValue`. Used by FE form-state, FE picker rendering, and
 * BE input validation to decide whether to require / surface the value
 * field, narrowing the operator to its value-carrying subset.
 */
export const operatorCarriesValue = (
  operator: CustomFieldConditionOperator
): operator is CustomFieldConditionOperatorCarryingValue =>
  OPERATOR_META[operator].carriesValue;
